/**
 * 도메인 타입 — docs/02_domain/01_db-spec.md 의 28개 테이블을 그대로 반영한다.
 *
 * DB 컨벤션(§0)
 * - Enum 은 DB 에 **한국어 문자열(VARCHAR)** 로 저장된다. 아래 union 타입의 리터럴은
 *   DB 값과 문자 단위로 일치해야 한다. 임의로 영문화하지 말 것.
 * - 톤수 NUMERIC(12,3), 단가 INTEGER(원), 금액 BIGINT(원) — JSON 전송 시 number 로 받는다.
 * - 날짜는 `YYYY-MM-DD`, 일시는 ISO8601 문자열로 주고받는다.
 */

/** `YYYY-MM-DD` */
export type DateString = string;
/** ISO8601 일시 문자열 */
export type DateTimeString = string;
/** `YYYY-MM` */
export type MonthString = string;

// ---------------------------------------------------------------------------
// Enum (DB 저장값과 동일한 한국어 리터럴)
// ---------------------------------------------------------------------------

/** users.role */
/**
 * `users.role`. 계약 §4.1 은 사용자를 담당자·기사·**하도급업체 관리자**·시스템관리자로
 * 나누는데, 기사만 별도 테이블이고 나머지는 이 표의 역할이다.
 *
 * **포털 역할은 관리자가 아니다** — 관리자 화면(`ADMIN_ROLES`)에 들어오지 못하고
 * 자기 소속 범위의 포털만 쓴다.
 *
 * - `업체` — 하도급 운송사. 지시를 받고 송장을 올린다 (`/carrier/*`)
 * - `청구업체` — 매출처. 자기 청구분의 송장 사진을 받아 본다 (`/client/*`, 2026-09-23)
 */
export type UserRole = "시스템관리자" | "담당자" | "업체" | "청구업체";

/**
 * 소속 업체(`company_id`)가 **곧 범위**인 포털 역할.
 * 백엔드 `dependencies.PORTAL_ROLES` 와 짝이다 — 소속을 갖는다는 성질만 공유하고,
 * 서로 다른 포털을 쓰므로 API 가드는 역할을 하나씩 정확히 비교한다.
 */
export type PortalRole = Extract<UserRole, "업체" | "청구업체">;
/**
 * **순서가 뜻을 갖는다** — 기능 집합에서 대표 역할을 고를 때 이 차례로 본다(백엔드
 * `dependencies.primary_portal_role`). 운송업체 기능이 있으면 대표는 `업체` 다.
 */
export const PORTAL_ROLES: readonly PortalRole[] = ["업체", "청구업체"];

/** 화면에 적는 이름. 저장되는 값은 `업체` 인데 **글자는 `운송업체`** 다 (담당자 요청 2026-10-02). */
export const PORTAL_ROLE_LABEL: Record<PortalRole, string> = {
  업체: "운송업체",
  청구업체: "청구업체",
};

/** 관리자 웹에 들어올 수 있는 역할. 백엔드 `dependencies.ADMIN_ROLES` 와 짝이다. */
export type AdminRole = Exclude<UserRole, PortalRole>;
/** users.status */
export type UserStatus = "활성" | "비활성";

/** drivers.driver_type */
export type DriverType = "소속기사" | "개인사업자" | "외주기사";
/** drivers.auth_source — 결정 #7 */
export type DriverAuthSource = "자체" | "1차";
/** drivers.status */
export type DriverStatus = "활성" | "휴직" | "퇴사";

/** locations.location_type */
export type LocationType = "상차지" | "하차지" | "상하차지";
/** location_aliases.source */
export type LocationAliasSource = "MES" | "배차" | "송장" | "수기";

/** item_mappings.source */
export type ItemMappingSource = "MES" | "배차" | "송장";
/** item_mappings.status */
export type ItemMappingStatus = "매핑완료" | "미분류";

/** routes.direction, dispatch_trips.direction, performances.direction, monthly_plans.direction */
export type Direction = "상행" | "하행";

/** vehicles.status */
export type VehicleStatus = "운행가능" | "정비중" | "폐차";

/** sales_prices.rate_type, purchase_prices.rate_type — 결정 #6 */
export type RateType = "톤당" | "회당";
/** purchase_prices.target_type */
export type PurchaseTargetType = "개인사업자" | "소속기사" | "외주업체";

/** excel_uploads.upload_type */
export type ExcelUploadType =
  | "배차"
  | "MES상행실적"
  | "상행운송내역"
  | "차량등록정보"
  | "기준정보"
  /** 월말 마감 엑셀 — 하행 송장 사진 없이 톤수를 확정한다 (2026-09-16) */
  | "마감실적";
/** excel_uploads.status */
export type ExcelUploadStatus = "검증중" | "미리보기" | "처리완료" | "실패";
/** excel_upload_rows.status */
export type ExcelUploadRowStatus = "정상" | "오류" | "중복";

/** dispatches.dispatch_type */
export type DispatchType = "소속" | "외부";
/** dispatches.status */
export type DispatchStatus = "운행예정" | "운행중" | "완료" | "취소";
/** dispatches.source — 결정 #2 */
/** `MES` 는 MES 반영이 자사 차량에 자동으로 만든 배차다 (2026-09-10) */
export type DispatchSource = "화면" | "엑셀" | "MES";
/** dispatch_trips.status */
export type DispatchTripStatus = "예정" | "진행" | "완료";

/** invoices.invoice_type */
/**
 * 송장 구분. **`미분류` 는 저장된 값이지 고를 수 있는 값이 아니다** — 업체가 구간별로
 * 몰아 올릴 때 들어가고, 담당자가 검수하며 상차·하차로 고른다. 분류하기 전에는
 * 검증을 완료할 수 없어 실적이 잘못 잡히지 않는다.
 */
export type InvoiceType = "상차" | "하차" | "미분류";

/** 사람이 고를 수 있는 구분. 제출·수정 요청에는 이쪽을 쓴다 */
export type InvoiceTypeChoice = "상차" | "하차";
/** invoices.ocr_provider — 결정 #5 */
export type OcrProvider = "clova" | "llm";
/** invoices.ocr_status */
export type OcrStatus = "대기" | "처리중" | "완료" | "실패";
/** invoices.verification_status */
export type InvoiceVerificationStatus =
  | "확인중"
  | "검증완료"
  | "반려"
  | "미매칭"
  | "중복";
/** invoices.match_type */
export type InvoiceMatchType =
  | "자동일치"
  | "후보일치"
  | "불일치"
  | "중복의심"
  | "배차없음"
  // 업체 차량 — 배차가 없는 것이 정상이라 담당자가 할 일이 없다. `배차없음` 과 나눠서
  // 미매칭 더미에 쌓이지 않게 하고 매칭 알림도 만들지 않는다.
  | "업체귀속";

/** mes_records.match_status */
// 원장에는 선별 대상 상세행만 들어간다. 회전수 소계행과 대상 외 상세행은 적재하지 않고
// 업로드 원본(`excel_upload_rows`)에만 남아 검산에 쓰인다.
//
// 실적이 되는 길은 둘이다. `매칭완료` 는 배차 회차에 붙은 것(자사 11대), `업체귀속` 은
// 배차가 없어 차량의 소속 운송사에 귀속시킨 것(업체 차량)이다 — 업체는 구간·품목·대수만
// 받고 어느 차가 뛸지 스스로 정하므로 배차가 없는 것이 정상이다.
export type MesMatchStatus = "매칭완료" | "업체귀속" | "미매칭" | "중복";

/** performances.status */
export type PerformanceStatus = "가실적" | "확정";

/** performances.origin — 배차에서 파생됐는지, 배차 없이 업체에 귀속됐는지 */
export type PerformanceOrigin = "배차" | "업체귀속";
/** performances.confirm_source */
export type PerformanceConfirmSource = "MES" | "송장" | "수동";
/** performance_confirmations.scope */
export type PerformanceConfirmScope = "건별" | "일자별" | "월별" | "업체별";
/** performance_confirmations.action */
export type PerformanceConfirmAction = "확정" | "확정취소";

/** settlements.settlement_type */
export type SettlementType = "매출" | "매입";
/** settlements.period_type */
export type SettlementPeriodType =
  | "월별"
  | "분기"
  | "상반기"
  | "하반기"
  | "년도별"
  | "기간";
/** settlements.status */
export type SettlementStatus = "가집계" | "확정";

/** documents.doc_type */
export type DocumentType = "거래명세서" | "세금계산서";
/** documents.issue_status */
export type DocumentIssueStatus = "미발행" | "발행완료";

/** notifications.notif_type — 결정 #9 (외부 발송 없음) */
export type NotificationType =
  | "송장미제출"
  | "매칭불일치"
  | "미확정실적"
  | "업로드오류"
  | "재업로드요청"
  // 기사 지원 업무 — 기사↔담당자 양방향
  | "이의신청접수"
  | "이의신청회신"
  | "차량변경요청"
  | "차량변경처리";

/**
 * `audit_logs.actor_type` — 닫힌 집합이다. `audit._actor_fields` **한 함수**가 정하고
 * `VARCHAR(10)` 이라 그 함수를 고치지 않고 늘어날 길이 없다.
 */
export type AuditActorType = "user" | "driver" | "system";
/**
 * `audit_logs.action` 은 **유니온으로 두지 않는다.** 자유 문자열이고 CHECK 가 없어
 * 14곳의 호출부가 동사를 자유롭게 더한다 — 여기 8종을 적어 둔 동안 실제는 14종이었고
 * 아무도 알아채지 못했다(타입스크립트는 서버 응답을 검증하지 않는다).
 * 드롭다운 후보는 `GET /api/audit-logs/filters` 가 DB 에서 뽑는다.
 */

/** driver_disputes.issue_type */
export type DisputeIssueType = "톤수불일치" | "단가오류" | "누락" | "기타";
/** driver_disputes.status */
export type DisputeStatus = "접수" | "검토중" | "완료";

/** vehicle_change_requests.status */
export type VehicleChangeRequestStatus = "접수" | "승인" | "반려";

// ---------------------------------------------------------------------------
// 공통
// ---------------------------------------------------------------------------

export interface Timestamped {
  created_at: DateTimeString;
  updated_at?: DateTimeString | null;
}

/** 목록 API 공통 응답 */
export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
}

/** 드롭다운 옵션(범주형 입력은 전부 dropdown + 검색 — 요구사항 §9-3) */
export interface Option<V extends string | number = string> {
  label: string;
  value: V;
  /** 보조 설명 (예: 차량번호 옆 소속 업체) */
  hint?: string;
  disabled?: boolean;
  /**
   * 값 자체가 색을 갖는 경우 (`#RRGGBB`). 지금은 구간뿐이다 — 목록과 고른 값 모두
   * 그 색을 배경으로 깐다(2026-09-17). 색이 없으면 그냥 글자다.
   */
  color?: string | null;
}

// ---------------------------------------------------------------------------
// 2. 사용자·인증
// ---------------------------------------------------------------------------

/** `users` */
export interface User extends Timestamped {
  id: number;
  user_id: string;
  name: string;
  role: UserRole;
  phone?: string | null;
  status: UserStatus;
  last_login_at?: DateTimeString | null;
  /** 포털 역할일 때만. 이 계정이 볼 수 있는 범위다 */
  company_id?: number | null;
  company_name?: string | null;
  /**
   * 그 계정이 **쓸 수 있는 포털 기능 전부** (마이그레이션 043 · 고객 질문 2026-10-15).
   * 포털 계정이 아니면 빈 배열이고, 포털 계정이면 `role` 을 반드시 포함한다.
   *
   * **탭과 라우트 가드는 이 목록으로 세운다** — `role` 하나만 보면 둘 다 켠 계정이
   * 한쪽 포털에서 *접근 권한이 없습니다* 를 본다.
   */
  portal_roles: PortalRole[];
}

/** `drivers` */
export interface Driver extends Timestamped {
  id: number;
  driver_code: string;
  name: string;
  phone?: string | null;
  driver_type: DriverType;
  company_id?: number | null;
  company_name?: string | null;
  /**
   * 거래명세서 공급자로 찍을 사업자(거래처) — 개인 차주 명세서의 등록번호·상호·주소가 여기서 온다
   * (050, 2026-10-30). **소속 운송사와 다른 축**이다 — 그쪽은 매입을 어디로 치를지 정한다
   */
  statement_company_id?: number | null;
  external_employee_id?: string | null;
  auth_source: DriverAuthSource;
  joined_date?: DateString | null;
  status: DriverStatus;
}

/** 토큰 주체 구분 (backend `TokenResponse.actor_type`) */
export type ActorType = "user" | "driver";

/**
 * 화면 권한 판정에 쓰는 역할.
 * `users.role` 을 쓰는 역할들은 그대로 쓰고, 기사는 주체 자체가 역할이다.
 */
export type AppRole = UserRole | "기사";

/**
 * 인증 응답에 실리는 사용자 정보 (backend `schemas.auth.UserInfo`).
 * `users` 테이블 전체가 아니라 비밀번호·타임스탬프를 뺀 부분집합이다.
 */
export type AuthUser = Omit<User, "created_at" | "updated_at">;

/** 인증 응답에 실리는 기사 정보 (backend `schemas.auth.DriverInfo`) */
export type AuthDriver = Omit<Driver, "created_at" | "updated_at" | "company_name">;

export interface LoginRequest {
  user_id: string;
  password: string;
}

export interface DriverLoginRequest {
  driver_code: string;
  password: string;
}

export interface RefreshRequest {
  refresh_token: string;
}

export interface PasswordChangeRequest {
  current_password: string;
  /** 8자 이상 */
  new_password: string;
}

/** 로그인·토큰 갱신 응답. 주체 유형에 따라 user 또는 driver 중 하나만 채워진다. */
export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  /** 액세스 토큰 만료까지 남은 초 */
  expires_in: number;
  actor_type: ActorType;
  user?: AuthUser | null;
  driver?: AuthDriver | null;
}

/** `GET /api/auth/me` 응답 */
export interface MeResponse {
  actor_type: ActorType;
  user?: AuthUser | null;
  driver?: AuthDriver | null;
}

/** 단순 성공 응답 (backend `schemas.common.MessageResponse`) */
export interface MessageResponse {
  success: boolean;
  message: string;
}

/** 공통 에러 응답 — docs/03_api/02_api-error-policy.md */
export interface ApiErrorBody {
  success: false;
  error: {
    code: string;
    /** 사용자에게 그대로 노출하는 한국어 메시지 */
    message: string;
    detail?: string | null;
    field?: string | null;
  };
  timestamp: string;
}

// ---------------------------------------------------------------------------
// 3. 기준정보
// ---------------------------------------------------------------------------

/** `companies` — 다중 역할(화주/상품소유업체/운송사)을 불리언 플래그로 중복 허용 */
export interface Company extends Timestamped {
  id: number;
  name: string;
  short_name?: string | null;
  /**
   * 담당자가 6년째 쓰는 레거시 거래처 코드(`H1100`). 마감 실적 양식의 `운송사CODE` 가
   * 이 값이고, **차량이 등록되지 않은 하행 운행의 업체를 알아내는 두 번째 축**이다.
   * 한중이 부여하는 번호가 아니라 받아 쓰는 번호다.
   */
  legacy_code?: string | null;
  /** 옛 거래처 코드(`H1100`). 읽기 전용 — 전환 기간 조회용 */
  previous_code?: string | null;
  biz_number?: string | null;
  ceo_name?: string | null;
  biz_type?: string | null;
  biz_item?: string | null;
  address?: string | null;
  contact?: string | null;
  email?: string | null;
  is_client: boolean;
  is_cargo_owner: boolean;
  is_carrier: boolean;
  is_active: boolean;
  sort_order: number;
  memo?: string | null;
}

/**
 * `locations`
 *
 * **소유 업체를 두지 않는다.** 매출 청구처는 배차에서 고른 값이거나 그 구간의 수요가로만
 * 정한다 — 장소로 유추하던 갈래를 2026-08-26 에 걷어냈다.
 */
export interface Location extends Timestamped {
  id: number;
  name: string;
  location_type: LocationType;
  address?: string | null;
  /** `HH:mm` */
  available_from?: string | null;
  /** `HH:mm` */
  available_to?: string | null;
  is_active: boolean;
  is_mes_upbound_destination: boolean;
  sort_order: number;
  memo?: string | null;
  aliases?: LocationAlias[];
}

/** `location_aliases` */
export interface LocationAlias {
  id: number;
  location_id: number;
  alias: string;
  source: LocationAliasSource;
  created_at: DateTimeString;
}

/** `items` */
export interface Item extends Timestamped {
  id: number;
  code: string;
  name: string;
  category?: string | null;
  /** 하행 소계 분류 ID — 비어 있으면 기본 분류(`기타`) (2026-10-20) */
  subtotal_category_id?: number | null;
  unit: string;
  /** `수재`만 true — 상행 전용 품목 */
  is_upbound_only: boolean;
  is_active: boolean;
  sort_order: number;
}

/** `item_mappings` — 원본 품목명 → 표준 품목 정규화 */
export interface ItemMapping extends Timestamped {
  id: number;
  source: ItemMappingSource;
  original_name: string;
  item_id?: number | null;
  item_name?: string | null;
  suggested_item_id?: number | null;
  suggested_item_name?: string | null;
  status: ItemMappingStatus;
  occurrence_count: number;
  last_seen_at?: DateTimeString | null;
}

/** `routes` */
export interface Route extends Timestamped {
  id: number;
  departure_location_id: number;
  departure_location_name?: string | null;
  arrival_location_id: number;
  arrival_location_name?: string | null;
  /**
   * **장소 자체**의 사용 여부 (2026-10-16). 아래 `is_active`(구간이 꺼져 있다)와
   * 다른 축이다 — 구간은 살아 있는데 그 장소가 미사용인 상태가 실재한다(운영 21건).
   * 담당자가 장소를 정리한 뒤 구간까지 옮겼는지 확인할 수 있도록 표가 이것을 적는다.
   */
  departure_is_active?: boolean;
  arrival_is_active?: boolean;
  direction: Direction;
  default_item_id?: number | null;
  default_item_name?: string | null;
  /**
   * 가톤수. 화면 라벨은 구간·단가 두 화면 모두 `가톤수` 다 (2026-10-14 통일).
   * **가실적의 마지막 폴백** — 배차 예상톤수·일일실적 가실적이
   * 값을 못 찾으면 여기서 가져온다 (고객 협의 2026-09-16, 관리 단위는 구간별).
   * 단가 화면에도 같은 값을 고치는 칸이 있지만 저장처는 언제나 구간 한 곳이다.
   */
  default_tonnage?: number | null;
  /**
   * 표에서 구간 이름에 칠할 배경색 (`#RRGGBB`). **비어 있는 것이 정상**이고,
   * 없으면 그냥 글자로 떨어진다 (고객 요청 2026-09-16 — 구간이 서른 줄 넘게 늘어설 때
   * 한눈에 가르기 위한 것이라 색은 거들 뿐이다).
   */
  color?: string | null;
  distance_km?: number | null;
  /**
   * 하행 실적을 어느 송장의 톤수로 확정할지 — 기본 `하차` (담당자 협의 2026-09-10).
   * 구간마다 마감 기준이 다르다. 기준 송장 한 장만 검증되면 실적이 확정된다.
   */
  performance_basis: PerformanceBasis;
  is_active: boolean;
  /**
   * 아래 넷은 **매출단가에서 유도한 읽기 전용 값**이다 (고객 요청 2026-10-14 —
   * *"장소구간탭 구간메뉴에 청구처도 같이 나오게"*). 저장처는 단가 마스터 한 곳이라
   * 이 화면에서는 고치지 않는다.
   *
   * `client_company_count` 가 2 이상이면 **청구처가 자동으로 안 정해진다** — 그때
   * 담당자는 배차나 송장 검수에서 직접 골라야 한다. 이 열의 값어치가 그 줄을
   * 드러내는 데 있다 (실측 650구간 중 44구간).
   */
  client_company_id?: number | null;
  client_company_name?: string | null;
  client_company_count?: number;
  client_company_names?: string[];
  /**
   * 사용 중 매출단가가 걸린 품목 ID. **구간은 품목을 가르지 않는다**(2026-10-29 — 같은
   * 상·하차지는 한 구간) — 배차에서 품목을 고를 때 이 목록이 먼저 선다.
   */
  price_item_ids?: number[];
}

/** 하행 실적 확정 기준 송장 (구간 단위) */
export type PerformanceBasis = "상차" | "하차";

/** `vehicles` — 차량번호 표기 3종(전체/뒷4자리/MES코드)을 모두 보유 */
export interface Vehicle extends Timestamped {
  id: number;
  vehicle_number: string;
  plate_suffix?: string | null;
  mes_vehicle_code?: string | null;
  vehicle_type?: string | null;
  tonnage?: number | null;
  company_id?: number | null;
  company_name?: string | null;
  /** 자사 직영 차량(개인사업자 11대). MES 실적이 배차와 안 붙어도 미매칭으로 남는다 */
  is_own?: boolean;
  /** 배차 등록판 차량 순서(작을수록 위). 비면 차량번호순으로 뒤에 선다 (044) */
  board_order?: number | null;
  status: VehicleStatus;
  is_active: boolean;
  /**
   * 등록 응답에만 실린다. 업체가 기준정보에 없는 번호를 먼저 적어 둔 배차 줄이
   * 이 등록으로 몇 개나 이어 붙었는지 — 담당자에게 무슨 일이 일어났는지 알린다.
   */
  linked_carrier_order_count?: number;
  /**
   * 등록·번호 수정 응답에만 실린다. 송장이 읽은 번호가 이 차량인데 차가 비어 있던 장에
   * 이 차량을 이은 수 — 업체 송장 실적의 차량과 명세서 차량번호도 함께 바뀐다 (2026-11-03)
   */
  linked_invoice_count?: number;
}

/** `vehicle_driver_assignments` — 차량·기사·업체 3자 매핑 이력(기간 중복 불가) */
export interface VehicleDriverAssignment {
  id: number;
  vehicle_id: number;
  vehicle_number?: string | null;
  driver_id: number;
  driver_name?: string | null;
  company_id?: number | null;
  company_name?: string | null;
  assigned_from: DateString;
  /** NULL = 현재 적용중 */
  assigned_to?: DateString | null;
  created_by?: number | null;
  created_at: DateTimeString;
}

/**
 * `sales_prices`
 * NULL 컬럼은 '무관'을 뜻한다. `company_id` 가 비어 있으면 **전체 거래처 공통 단가**이며,
 * 레거시 역산 데이터에서는 이쪽이 오히려 정상 케이스다.
 */
export interface SalesPrice extends Timestamped {
  id: number;
  /** 계약 단가표의 운송코드 — 같은 코드의 매입 단가와 짝을 이룬다 */
  contract_code?: string | null;
  /** 옛 운송코드(`G0104`). 040 이 `a1`~ 로 바꿀 때 남긴 원문 */
  legacy_contract_code?: string | null;
  company_id?: number | null;
  company_name?: string | null;
  route_id?: number | null;
  /** 백엔드 `SalesPriceResponse.route_name` — `상차지→하차지` */
  route_name?: string | null;
  item_id?: number | null;
  item_name?: string | null;
  vehicle_type?: string | null;
  rate_type: RateType;
  unit_price: number;
  applies_from: DateString;
  applies_to?: DateString | null;
  is_active: boolean;
  memo?: string | null;
  /**
   * 이 (구간·품목)에 매출단가가 붙은 **거래처 수** — 거래처를 비운 공통 단가도 한 곳으로
   * 센다. 펼치지 않고도 갈린 구간을 알아보는 값이고, **활성 기준 2 이상이면 청구처가
   * 자동으로 안 정해진다**(`invoice._contract_client_company_id`).
   */
  client_count?: number | null;
}

/**
 * **한 (구간·품목)의 매출단가 묶음** — 단가 화면이 구간 행 하나로 접어 보여 준다.
 *
 * 모양은 Atlassian 의 *table tree* 를 따랐다(2026-10-13) — 머리행이 신원(구간·품목)을
 * 지고 자식은 **같은 열을 그대로 쓰는 진짜 행**이다. 끼워 넣는 패널(Carbon 의
 * *expandable row*)은 한 행의 부가 설명용이라 계층에는 맞지 않는다.
 */
export interface GroupedSalesPrice {
  /** 묶음 키 `route|item` — 표의 행 식별자이자 선택 키 */
  key: string;
  route_id?: number | null;
  route_name?: string | null;
  item_id?: number | null;
  item_name?: string | null;
  /** 거래처 수. **거르지 않은 전체**를 센다 — 자식 수와 다를 수 있다(필터) */
  client_count: number;
  unit_price_min: number;
  unit_price_max: number;
  /** 거래처명 순, 거래처를 비운 공통 단가는 맨 뒤 */
  prices: SalesPrice[];
}

/** `purchase_prices` */
export interface PurchasePrice extends Timestamped {
  id: number;
  /** 계약 단가표의 운송코드 — 기사·업체 두 건을 한 줄로 되묶는 열쇠 */
  contract_code?: string | null;
  /** 옛 운송코드(`G0104`). 040 이 `a1`~ 로 바꿀 때 남긴 원문 */
  legacy_contract_code?: string | null;
  target_type: PurchaseTargetType;
  company_id?: number | null;
  company_name?: string | null;
  driver_id?: number | null;
  driver_name?: string | null;
  route_id?: number | null;
  /** 백엔드 `PurchasePriceResponse.route_name` — `상차지→하차지` */
  route_name?: string | null;
  item_id?: number | null;
  item_name?: string | null;
  rate_type: RateType;
  unit_price: number;
  applies_from: DateString;
  applies_to?: DateString | null;
  is_active: boolean;
  memo?: string | null;
}

/** 매입 단가 묶음의 한쪽 — 기사 또는 업체 */
export interface PurchasePriceSide {
  id: number;
  target_type: PurchaseTargetType;
  rate_type: RateType;
  unit_price: number;
  is_active: boolean;
}

/** 구간 단가 줄의 매출 칸 */
export interface SalesPriceSide {
  id: number;
  rate_type: RateType;
  unit_price: number;
  is_active: boolean;
}

/**
 * 구간 화면의 단가 한 줄 — 계약 한 줄(매출 1 + 매입 기사·업체 2).
 * 매출과 매입은 운송코드로, 코드가 없으면 (품목·적용기간)으로 짝을 짓는다 (2026-10-30).
 */
export interface RoutePriceLine {
  key: string;
  item_id?: number | null;
  item_name?: string | null;
  company_id?: number | null;
  company_name?: string | null;
  contract_code?: string | null;
  applies_from: DateString;
  applies_to?: DateString | null;
  sales?: SalesPriceSide | null;
  driver?: PurchasePriceSide | null;
  company?: PurchasePriceSide | null;
}

export interface RoutePriceLineList {
  items: RoutePriceLine[];
  /** 업체·기사를 지정한 매입 단가 건수 — 이 목록에는 담기지 않는다 */
  targeted: number;
}

export interface RoutePriceLineInput {
  item_id?: number | null;
  company_id?: number | null;
  contract_code?: string | null;
  rate_type?: RateType;
  sales_unit_price?: number | null;
  driver_unit_price?: number | null;
  company_unit_price?: number | null;
  applies_from: DateString;
  applies_to?: DateString | null;
}

/**
 * 구간·품목 하나에 기사 단가와 업체 단가를 나란히 담은 줄.
 * 계약 단가표가 원래 이 모양이다 — 운송코드 한 줄에 `매입단가|기사`·`매입단가|업체` 두 칸.
 */
export interface PairedPurchasePrice {
  key: string;
  contract_code?: string | null;
  /** 옛 운송코드(`G0104`). 040 이 `a1`~ 로 바꿀 때 남긴 원문 */
  legacy_contract_code?: string | null;
  route_id?: number | null;
  route_name?: string | null;
  item_id?: number | null;
  item_name?: string | null;
  applies_from: DateString;
  applies_to?: DateString | null;
  driver?: PurchasePriceSide | null;
  company?: PurchasePriceSide | null;
  memo?: string | null;
}

/**
 * 한 (구간·품목)의 매입 짝 단가 전부 — 화면이 구간 행 하나로 접고, 머리행에 현재 단가,
 * 펼치면 지난 단가까지 보여 준다(고객 요청 2026-10-22). 매출 묶음과 같은 꼴이고 자식만 짝이다.
 */
export interface GroupedPurchasePrice {
  /** 묶음 키 `route|item` — 표의 행 식별자이자 선택 키 */
  key: string;
  route_id?: number | null;
  route_name?: string | null;
  item_id?: number | null;
  item_name?: string | null;
  /** 자식 짝 단가 — 운송코드 순(신규 위로) */
  prices: PairedPurchasePrice[];
}

/** `monthly_plans` — 일일실적 집계표의 계획(A)·일일계획(C) 원천 */
export interface MonthlyPlan extends Timestamped {
  id: number;
  plan_month: MonthString;
  direction: Direction;
  item_id: number;
  item_name?: string | null;
  route_id: number;
  /** 백엔드 `MonthlyPlanResponse.route_name` — `상차지→하차지` */
  route_name?: string | null;
  planned_tonnage: number;
  planned_amount?: number | null;
  working_days: number;
  /** 서버 계산값 — 월 계획 물량 ÷ 작업일수 */
  daily_planned_tonnage?: number | null;
}

/** `GET /api/master/item-mappings/summary` — 미분류 건수 요약 */
export interface ItemMappingSummary {
  total: number;
  mapped: number;
  unclassified: number;
  by_source: { source: ItemMappingSource; unclassified: number }[];
}

/**
 * `GET /api/master/prices/resolve` — 적용 단가 조회 결과.
 * 단가가 없으면 서버가 400 `PRICE_NOT_FOUND` 를 돌려준다.
 */
export interface PriceResolveResult {
  price_kind: SettlementType;
  price_id: number;
  rate_type: RateType;
  unit_price: number;
  applies_from: DateString;
  applies_to?: DateString | null;
  company_id?: number | null;
  driver_id?: number | null;
  route_id?: number | null;
  item_id?: number | null;
  vehicle_type?: string | null;
  tonnage?: number | null;
  trip_count?: number | null;
  supply_amount: number;
  tax_amount: number;
  total_amount: number;
}

// ---------------------------------------------------------------------------
// 4. 엑셀 업로드 공통
// ---------------------------------------------------------------------------

/** `excel_uploads` */
export interface ExcelUpload {
  id: number;
  upload_type: ExcelUploadType;
  file_name: string;
  file_path: string;
  target_date?: DateString | null;
  target_period?: string | null;
  mapping_profile_id?: number | null;
  total_rows: number;
  success_rows: number;
  error_rows: number;
  duplicate_rows: number;
  status: ExcelUploadStatus;
  error_file_path?: string | null;
  uploaded_by?: number | null;
  uploaded_by_name?: string | null;
  created_at: DateTimeString;
}

export interface ExcelRowError {
  field: string;
  message: string;
}

/** `excel_upload_rows` — 원본 행 스냅샷 */
export interface ExcelUploadRow {
  id: number;
  upload_id: number;
  row_no: number;
  raw: Record<string, unknown>;
  status: ExcelUploadRowStatus;
  error_messages?: ExcelRowError[] | null;
  created_at: DateTimeString;
}

export interface ColumnMappingEntry {
  system_field: string;
  excel_column: string;
  required: boolean;
}

/** `column_mapping_profiles` — 결정 #8 */
export interface ColumnMappingProfile extends Timestamped {
  id: number;
  upload_type: ExcelUploadType;
  name: string;
  mappings: ColumnMappingEntry[];
  is_default: boolean;
  created_by?: number | null;
}

// ---------------------------------------------------------------------------
// 5. 배차
// ---------------------------------------------------------------------------

/** `dispatches` — 배차가 실적·정산의 단일 원천(SoT) */
export interface Dispatch extends Timestamped {
  id: number;
  /** `DSP-YYYYMMDD-nnn` */
  dispatch_no: string;
  dispatch_date: DateString;
  dispatch_type: DispatchType;
  vehicle_id?: number | null;
  vehicle_number?: string | null;
  driver_id?: number | null;
  driver_name?: string | null;
  company_id?: number | null;
  company_name?: string | null;
  status: DispatchStatus;
  source: DispatchSource;
  upload_id?: number | null;
  memo?: string | null;
  created_by?: number | null;
  /** 상행 1건 + 하행 1~3건 */
  trips?: DispatchTrip[];
  /** 중복 배차 등 경고 (차단하지 않는다). `force` 로 통과시켰을 때 채워진다 */
  warnings?: string[];
}

/**
 * `dispatch_trips` — 실적·송장·정산이 모두 이 테이블을 기준으로 파생된다.
 * 백엔드 `DispatchTripResponse` 와 1:1. 조인된 장소·품목·업체명이 함께 실려 온다.
 */
export interface DispatchTrip extends Timestamped {
  id: number;
  dispatch_id: number;
  direction: Direction;
  /** 상행=1, 하행=1..3 */
  trip_no: number;
  route_id?: number | null;
  /** 구간명 (`상차지→하차지`). 서버가 조인해 준다 */
  route_name?: string | null;
  loading_location_id?: number | null;
  loading_location_name?: string | null;
  unloading_location_id?: number | null;
  unloading_location_name?: string | null;
  item_id?: number | null;
  item_name?: string | null;
  cargo_owner_company_id?: number | null;
  cargo_owner_company_name?: string | null;
  /** 청구처. 비우면 도착지·구간 계약에서 서버가 유도한다 */
  client_company_id?: number | null;
  client_company_name?: string | null;
  trip_count: number;
  /** Decimal 필드는 서버가 JSON 숫자로 내보낸다 (`app.schemas.common.Quantity`) */
  expected_tonnage?: number | null;
  actual_loading_tonnage?: number | null;
  actual_unloading_tonnage?: number | null;
  /** 상행은 false — 상행에는 송장이 없다 */
  invoice_required: boolean;
  /** 상차 송장 검증 상태. 미제출이면 null */
  loading_invoice_status?: InvoiceVerificationStatus | string | null;
  /** 하차 송장 검증 상태. 미제출이면 null */
  unloading_invoice_status?: InvoiceVerificationStatus | string | null;
  status: DispatchTripStatus;
}

/**
 * 배차현황 표의 한 줄 — **회차 1건 = 표 1행**이다.
 * 배차 1건은 상행 1행 + 하행 N행으로 펼쳐지고, 배차 단위 정보(차량·기사·업체)는 모든 행에 반복된다.
 * 화면은 병합 지표 두 쌍으로 셀만 합치면 된다 — 차량·기사·업체는 **차량 단위**
 * (`is_vehicle_first_row`/`vehicle_row_span`), 상태·상세·삭제는 **배차 단위**
 * (`is_first_row`/`row_span`). 뒤쪽까지 차량으로 넓히면 두 번째 배차를 열 수 없다.
 */
export interface DispatchListRow {
  dispatch_id: number;
  dispatch_no: string;
  dispatch_date: DateString;
  dispatch_type: DispatchType;
  status: DispatchStatus;
  source: DispatchSource;
  memo?: string | null;
  vehicle_id?: number | null;
  vehicle_number?: string | null;
  driver_id?: number | null;
  driver_name?: string | null;
  company_id?: number | null;
  company_name?: string | null;

  trip_id: number;
  direction: Direction;
  trip_no: number;
  route_id?: number | null;
  route_name?: string | null;
  loading_location_id?: number | null;
  loading_location_name?: string | null;
  unloading_location_id?: number | null;
  unloading_location_name?: string | null;
  item_id?: number | null;
  item_name?: string | null;
  cargo_owner_company_id?: number | null;
  cargo_owner_company_name?: string | null;
  /** 청구처. 비우면 도착지·구간 계약에서 서버가 유도한다 */
  client_company_id?: number | null;
  client_company_name?: string | null;
  trip_count: number;
  expected_tonnage?: number | null;
  actual_loading_tonnage?: number | null;
  actual_unloading_tonnage?: number | null;
  invoice_required: boolean;
  loading_invoice_status?: InvoiceVerificationStatus | string | null;
  unloading_invoice_status?: InvoiceVerificationStatus | string | null;
  trip_status: DispatchTripStatus;

  /** 배차의 첫 행 여부 (셀 병합 기준) */
  /** 배차의 첫 행 — 상태·상세·삭제 칸을 여는 기준 */
  is_first_row: boolean;
  /** 첫 행에서 병합할 행 수 = 그 배차의 회차 수 */
  row_span: number;
  /**
   * 차량 묶음의 첫 행 — 차량·기사·업체 칸을 여는 기준.
   * 같은 차량에 배차가 둘이면(하루 두 번 운행·MES 자동배차) 그 둘이 한 묶음이다.
   */
  is_vehicle_first_row: boolean;
  /** 그 차량 배차들의 회차 수 합계 */
  vehicle_row_span: number;
}

// ---------------------------------------------------------------------------
// 6. 송장·OCR
// ---------------------------------------------------------------------------

export interface InvoiceMismatchReason {
  field: string;
  ocr_value?: string | number | null;
  dispatch_value?: string | number | null;
}

/** `invoices` */
export interface Invoice extends Timestamped {
  id: number;
  /** NULL = 미매칭 */
  dispatch_trip_id?: number | null;
  /**
   * 업체 배차. 업체·담당자가 배차를 지목해 올리면 여기 붙는다.
   * 기사 송장에는 없는 것이 정상이다 — 그쪽은 회차(`dispatch_trip_id`)로 붙는다.
   */
  carrier_order_id?: number | null;
  invoice_type: InvoiceType;
  image_path: string;
  thumbnail_path?: string | null;
  submitted_by_driver_id?: number | null;
  submitted_by_driver_name?: string | null;
  submitted_at: DateTimeString;
  transport_date?: DateString | null;
  vehicle_id?: number | null;
  vehicle_number?: string | null;

  // OCR — 결정 #5 (엔진 2종 어댑터, 엔진·신뢰도·원문 동시 저장)
  ocr_provider?: OcrProvider | null;
  ocr_status: OcrStatus;
  ocr_confidence?: number | null;
  ocr_raw?: Record<string, unknown> | null;
  ocr_text?: string | null;
  ocr_invoice_no?: string | null;
  ocr_transport_date?: DateString | null;
  ocr_vehicle_number?: string | null;
  ocr_driver_name?: string | null;
  ocr_loading_place?: string | null;
  ocr_unloading_place?: string | null;
  ocr_item_name?: string | null;
  ocr_total_weight?: number | null;
  ocr_empty_weight?: number | null;
  ocr_net_tonnage?: number | null;
  ocr_amount?: number | null;

  // 확정값 — 담당자 검수 결과
  final_item_id?: number | null;
  final_item_name?: string | null;
  final_tonnage?: number | null;
  final_loading_location_id?: number | null;
  final_loading_location_name?: string | null;
  final_unloading_location_id?: number | null;
  final_unloading_location_name?: string | null;

  // 검증
  verification_status: InvoiceVerificationStatus;
  match_type?: InvoiceMatchType | null;
  match_score?: number | null;
  mismatch_reasons?: InvoiceMismatchReason[] | null;
  duplicate_of_invoice_id?: number | null;
  verified_by?: number | null;
  verified_at?: DateTimeString | null;
  reject_reason?: string | null;
  /** 검증 완료 후 수정 시 필수 */
  modify_reason?: string | null;
}

export interface InvoiceMatchReason {
  field: string;
  matched: boolean;
  invoice_value?: string | number | null;
  dispatch_value?: string | number | null;
}

/** `invoice_match_candidates` — 상위 3건 추천 */
export interface InvoiceMatchCandidate {
  id: number;
  invoice_id: number;
  dispatch_trip_id: number;
  score: number;
  rank: number;
  reasons?: InvoiceMatchReason[] | null;
  created_at: DateTimeString;
  /** 화면 표시용 배차 요약 */
  dispatch_summary?: DispatchTrip | null;
}

// ---------------------------------------------------------------------------
// 7. MES
// ---------------------------------------------------------------------------

/** `mes_records` — 상차실적 원장. kg → 톤 환산은 실적 반영 시 수행 */
export interface MesRecord {
  id: number;
  upload_id: number;
  transport_date?: DateString | null;
  vehicle_code?: string | null;
  vehicle_number?: string | null;
  work_type?: string | null;
  loading_place?: string | null;
  hopper?: string | null;
  client_name?: string | null;
  destination?: string | null;
  order_no?: string | null;
  is_long_distance?: boolean | null;
  is_weighed?: boolean | null;
  weight_kg?: number | null;
  measured_weight_kg?: number | null;
  invoice_no?: string | null;
  matched_vehicle_id?: number | null;
  matched_dispatch_trip_id?: number | null;
  match_status: MesMatchStatus;
  created_at: DateTimeString;
}

// ---------------------------------------------------------------------------
// 8. 실적
// ---------------------------------------------------------------------------

/** `performances` — 확정 시점 단가·금액을 스냅샷으로 고정 */
export interface Performance extends Timestamped {
  id: number;
  performance_date: DateString;
  direction: Direction;
  /** 회차당 실적 1건 */
  dispatch_trip_id?: number | null;
  vehicle_id?: number | null;
  vehicle_number?: string | null;
  driver_id?: number | null;
  driver_name?: string | null;
  /** 운송사 — 매입 귀속 */
  company_id?: number | null;
  company_name?: string | null;
  /** 청구처 — 매출 귀속 */
  client_company_id?: number | null;
  client_company_name?: string | null;
  cargo_owner_company_id?: number | null;
  cargo_owner_company_name?: string | null;
  route_id?: number | null;
  route_label?: string | null;
  item_id?: number | null;
  item_name?: string | null;
  trip_count: number;
  expected_tonnage?: number | null;
  actual_tonnage?: number | null;
  sales_unit_price?: number | null;
  sales_rate_type?: RateType | null;
  sales_supply_amount?: number | null;
  sales_tax_amount?: number | null;
  purchase_unit_price?: number | null;
  purchase_rate_type?: RateType | null;
  purchase_supply_amount?: number | null;
  purchase_tax_amount?: number | null;
  status: PerformanceStatus;
  confirm_source?: PerformanceConfirmSource | null;
  confirmed_by?: number | null;
  confirmed_at?: DateTimeString | null;
}

/** `performance_confirmations` */
export interface PerformanceConfirmation {
  id: number;
  scope: PerformanceConfirmScope;
  action: PerformanceConfirmAction;
  performance_id?: number | null;
  target_date?: DateString | null;
  target_company_id?: number | null;
  affected_count: number;
  /** 확정취소는 사유 필수 */
  reason?: string | null;
  actor_id?: number | null;
  actor_name?: string | null;
  created_at: DateTimeString;
}

export interface DownboundEstimateSample {
  date: DateString;
  tonnage: number;
  /** 이상치 포함/제외 토글 상태 */
  included: boolean;
}

/** `downbound_estimates` — 하행 가실적 산출 근거(최근 5회 평균) */
export interface DownboundEstimate {
  id: number;
  dispatch_trip_id: number;
  recent_avg_tonnage?: number | null;
  estimated_tonnage?: number | null;
  samples?: DownboundEstimateSample[] | null;
  calculated_at: DateTimeString;
}

/**
 * 일일실적 현황 집계 노드 (REQ-P-03).
 * 상행/하행 > 품목 > 구간 계층. 구간마다 물량 행 + 금액 행 2줄로 표시한다.
 * 단위: 물량 톤, 금액 원.
 */
export interface DailyPerformanceRow {
  level: "방향" | "품목" | "구간";
  direction: Direction;
  item_id?: number | null;
  item_name?: string | null;
  route_id?: number | null;
  route_label?: string | null;
  /** A. 월계획 */
  plan_month: number;
  /** B. 실적(월 누계) */
  actual_month: number;
  /** B - A */
  plan_diff: number;
  /** B / A */
  achievement_rate: number;
  /** C. 일일계획 */
  plan_daily: number;
  /** D. 일일작업 */
  actual_daily: number;
  /** D - C */
  daily_diff: number;
  /** 남은일 계획량 */
  remaining_plan: number;
  /** 전일 누계 */
  prev_cumulative: number;
  /** 금일 누계 */
  today_cumulative: number;
  children?: DailyPerformanceRow[];
}

export interface DailyPerformanceSummary {
  target_date: DateString;
  rows: DailyPerformanceRow[];
}

// ---------------------------------------------------------------------------
// 9. 정산·문서
// ---------------------------------------------------------------------------

/** 정산 대상 구분. 매입은 운송회사(거래처)와 개인차주 둘 다 대상이 된다 */
export type SettlementPartyKind = "거래처" | "기사";
/**
 * 매입 집계의 큰 구분 — 외주 운송사냐 자사(개인차주)냐 (고객 요청 2026-10-14).
 *
 * **`vehicles.is_own` 이 아니라 `party_kind` 에서 파생한다.** 매입 명세서가
 * 운송회사·개인차주 축으로 나가므로 집계표가 다른 축을 쓰면 두 문서가 갈린다.
 */
export type SettlementPartyGroup = "업체" | "자차";

/** `settlements` */
export interface Settlement extends Timestamped {
  id: number;
  settlement_type: SettlementType;
  period_type: SettlementPeriodType;
  period_start: DateString;
  period_end: DateString;
  company_id?: number | null;
  driver_id?: number | null;
  /** 업체명 또는 기사명 */
  party_name?: string | null;
  total_trips: number;
  total_tonnage: number;
  supply_amount: number;
  tax_amount: number;
  total_amount: number;
  status: SettlementStatus;
  confirmed_by?: number | null;
  confirmed_at?: DateTimeString | null;
  line_count: number;
  document_count: number;
  lines?: SettlementLine[];
}

/**
 * `settlement_lines`
 * 공급가액 = FLOOR(톤수 × 단가) 또는 횟수 × 단가(회당 요율)
 * 부가세 = FLOOR(공급가액 / 10)
 */
export interface SettlementLine {
  id: number;
  settlement_id: number;
  performance_id?: number | null;
  performance_date?: DateString | null;
  vehicle_id?: number | null;
  vehicle_number?: string | null;
  route_id?: number | null;
  route_name?: string | null;
  item_id?: number | null;
  item_name?: string | null;
  trip_count: number;
  tonnage: number;
  unit_price: number;
  rate_type: RateType;
  supply_amount: number;
  tax_amount: number;
  total_amount: number;
  created_at: DateTimeString;
  /** 매입 품목 순서(작을수록 앞) — 응답에만 있는 값, 실적표 순서. 매출은 비어 있다 */
  statement_order?: number | null;
}

/** `settlement_changes` — 사유 필수 */
export interface SettlementChange {
  id: number;
  settlement_id: number;
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
  reason: string;
  actor_id?: number | null;
  actor_name?: string | null;
  created_at: DateTimeString;
}

/** `documents` — 결정 #4: 국세청/전자발행 API 연동 없음 */
export interface DocumentRecord {
  id: number;
  doc_type: DocumentType;
  settlement_id?: number | null;
  company_id: number;
  company_name?: string | null;
  period_start: DateString;
  period_end: DateString;
  doc_no: string;
  supply_amount: number;
  tax_amount: number;
  total_amount: number;
  file_path?: string | null;
  issue_status: DocumentIssueStatus;
  issued_date?: DateString | null;
  tax_invoice_no?: string | null;
  version: number;
  generated_by?: number | null;
  created_at: DateTimeString;
}

/** REQ-S-01 매출·매입 집계표 행. `row_type` 이 `구간` 이면 명세 행, 나머지는 합계 행 */
export interface SettlementSummaryRow {
  row_type: "구간" | "소계" | "총계";
  label: string;
  party_kind?: SettlementPartyKind | null;
  company_id?: number | null;
  driver_id?: number | null;
  party_name?: string | null;
  item_id?: number | null;
  item_name?: string | null;
  /** 분류별 보기에서 이 행이 선 분류. 업체별 보기에서는 비어 있다 */
  category?: string | null;
  route_id?: number | null;
  route_name?: string | null;
  loading_location_name?: string | null;
  unloading_location_name?: string | null;
  trip_count: number;
  /** 월말 조정을 반영한 확정(마감) 톤수 */
  tonnage: number;
  /**
   * 정산 톤수 — 포스코 수재(상행) 매입은 매입처·구간·단가마다 달마다 소수점을 버린다(2026-10-30).
   * 금액은 이 톤수로 낸 값이다. 해당 없는 줄은 비어 있다
   */
  settled_tonnage?: number | null;
  /** 월말 톤수 조정분 = 대당 조정치 × 횟수. **음수가 감량** */
  adjust_tonnage: number;
  supply_amount: number;
  tax_amount: number;
  total_amount: number;
  /** `구간 미지정` 행에만 — 그 안의 실적과 고칠 자리(송장·배차) (2026-10-28) */
  unrouted?: UnroutedPerformance[];
}

/** 구간이 빈 실적 한 건 — 무엇을 고치면 되는지 가리킨다 */
export interface UnroutedPerformance {
  performance_id: number;
  performance_date: DateString;
  status: string;
  invoice_id?: number | null;
  dispatch_id?: number | null;
  reason: string;
}

/** 집계표의 업체 그룹 — `rows` 가 품목·구간 행, `subtotal` 이 업체 소계 */
export interface SettlementSummaryGroup {
  party_kind: SettlementPartyKind;
  party_group: SettlementPartyGroup;
  company_id?: number | null;
  driver_id?: number | null;
  party_name: string;
  rows: SettlementSummaryRow[];
  subtotal: SettlementSummaryRow;
}

/** 분류별 보기의 한 묶음 — 분류 > 귀속처 > 구간 (고객 요청 2026-10-19) */
export interface SettlementSummaryCategoryGroup {
  direction?: "상행" | "하행" | null;
  /** 하행 소계 분류 ID. 상행은 비어 있다 */
  category_id?: number | null;
  /** 하행은 클링커 · 석회석 · 기타, 상행은 품목명(수재) */
  category: string;
  parties: SettlementSummaryGroup[];
  subtotal: SettlementSummaryRow;
}

/** `subtotal_categories` — 하행 소계 분류 마스터 (2026-10-20) */
export interface SubtotalCategory {
  id: number;
  name: string;
  /** 화면 순서 — 작을수록 위 */
  sort_order: number;
  /** 아무 데서도 정해지지 않은 실적이 들어가는 분류. 하나뿐이고 끌 수 없다 */
  is_fallback: boolean;
  is_active: boolean;
  /** 실적표 소계 줄 배경색 (`#RRGGBB`). 비면 색 없음 */
  color?: string | null;
  /** 쓰는 곳 요약 — `품목 1, 구간·품목 고정 12` */
  usage?: string | null;
  /** 0 일 때만 지울 수 있다 */
  usage_count: number;
}

/** REQ-S-01 매출·매입 집계표 */
export interface SettlementSummary {
  settlement_type: SettlementType;
  period_type: SettlementPeriodType;
  period_start: DateString;
  period_end: DateString;
  period_label: string;
  include_estimate: boolean;
  groups: SettlementSummaryGroup[];
  /** 분류별 보기 — `groups` 와 같은 숫자를 분류 > 귀속처 > 구간으로 묶은 것 */
  category_groups: SettlementSummaryCategoryGroup[];
  /** 구분(업체·자차)별 계. **둘 다 있을 때만** 채워진다 */
  group_totals: SettlementSummaryRow[];
  grand_total: SettlementSummaryRow;
}

/** 집계표 구간 행을 펼쳤을 때의 일별 내역 한 줄 (고객 요청 2026-10-14) */
export interface SettlementSummaryDailyRow {
  performance_date: DateString;
  vehicle_id?: number | null;
  /** 업체귀속 실적은 비어 있을 수 있다 — 업체가 어느 차를 넣었는지 안 적은 것이다 */
  vehicle_number?: string | null;
  /** 구간·품목 — 업체 하나로 부르면(운송사 필터) 화면이 이것으로 구간 행 아래에 나눠 꽂는다 */
  route_id?: number | null;
  item_id?: number | null;
  trip_count: number;
  tonnage: number;
  adjust_tonnage: number;
  /** 한 날에 단가가 여럿 섞이면 비어 있다 */
  unit_price?: number | null;
  supply_amount: number;
  tax_amount: number;
  total_amount: number;
}

/** 한 구간 줄의 일별 내역. `total` 은 그 구간 행과 같아야 한다 */
export interface SettlementSummaryDaily {
  settlement_type: SettlementType;
  period_start: DateString;
  period_end: DateString;
  rows: SettlementSummaryDailyRow[];
  total: SettlementSummaryDailyRow;
}

/** REQ-S-05 결산 검증 경고. `blocking` 이면 결산 확정을 막는다 */
export type SettlementWarningType =
  | "단가누락"
  | "미확정실적"
  | "품목미확정"
  | "중복의심"
  | "귀속중복"
  | "기간중복"
  | "대상미정";

export interface SettlementValidationWarning {
  type: SettlementWarningType;
  message: string;
  count: number;
  blocking: boolean;
  ref_type?: string | null;
  ref_id?: number | null;
}

/** 경고 하나에 걸린 실적 1건. 화면에서 바로 처리할 수 있을 만큼 담는다 */
export interface SettlementValidationDetailRow {
  performance_id: number;
  performance_date: DateString;
  direction: Direction;
  status: string;
  origin?: string | null;
  vehicle_number?: string | null;
  /** 매출은 청구처, 매입은 운송사·차주 */
  party_name?: string | null;
  route_id?: number | null;
  route_name?: string | null;
  /** 단가 빠른 등록을 채워서 열 때 쓴다 */
  departure_location_id?: number | null;
  arrival_location_id?: number | null;
  item_id?: number | null;
  item_name?: string | null;
  trip_count: number;
  tonnage: number;
  unit_price?: number | null;
  supply_amount: number;
  /** 매출 단가(실적 스냅샷) */
  sales_unit_price?: number | null;
  /** 매입 단가 — 기사(개인차주). 이 실적의 매입 대상이면 스냅샷, 아니면 지금 단가(참고) */
  purchase_driver_unit_price?: number | null;
  /** 매입 단가 — 업체. 이 실적의 매입 대상이면 스냅샷, 아니면 지금 단가(참고) */
  purchase_company_unit_price?: number | null;
  /** 이 실적의 매입 대상. 비면 매입 대상 미정 */
  purchase_target?: "기사" | "업체" | null;
  /** 매출 — 그 구간·품목·일자에 걸리는 단가. `company_id` 가 비면 전체 적용 */
  price_options?: SettlementPriceOption[];
  /** 미확정실적 — 아직 확정되지 않은 사유 */
  remarks?: string[];
}

export interface SettlementPriceOption {
  company_id?: number | null;
  company_name?: string | null;
  unit_price: number;
  rate_type: string;
}

export interface SettlementValidationDetail {
  type: SettlementWarningType;
  settlement_type: string;
  total: number;
  rows: SettlementValidationDetailRow[];
  truncated: boolean;
}

/** REQ-S-05 결산 검증 결과 */
export interface SettlementValidation {
  settlement_type: SettlementType;
  period_start: DateString;
  period_end: DateString;
  warnings: SettlementValidationWarning[];
  blocked: boolean;
}

// ---------------------------------------------------------------------------
// 9-1. 통계 (REQ-S-04)
// ---------------------------------------------------------------------------

export type StatisticsPeriodUnit = "월" | "분기" | "연";
export type StatisticsDimension = "품목" | "업체" | "차량" | "기사";

/** 한 구간의 지표. 이익 = 매출 − 매입 */
export interface StatisticsMetrics {
  tonnage: number;
  trip_count: number;
  sales_amount: number;
  purchase_amount: number;
  profit: number;
  profit_rate?: number | null;
}

export interface StatisticsTrendRow extends StatisticsMetrics {
  period: string;
  period_start: DateString;
  period_end: DateString;
  profit_diff?: number | null;
  sales_diff?: number | null;
  sales_growth_rate?: number | null;
}

export interface StatisticsTrend {
  unit: StatisticsPeriodUnit;
  date_from: DateString;
  date_to: DateString;
  include_estimate: boolean;
  rows: StatisticsTrendRow[];
  total: StatisticsMetrics;
}

export interface StatisticsBreakdownRow extends StatisticsMetrics {
  key_id?: number | null;
  label: string;
  share?: number | null;
}

export interface StatisticsBreakdown {
  dimension: StatisticsDimension;
  date_from: DateString;
  date_to: DateString;
  include_estimate: boolean;
  rows: StatisticsBreakdownRow[];
  total: StatisticsMetrics;
}

// ---------------------------------------------------------------------------
// 10. 공통
// ---------------------------------------------------------------------------

/** `notifications` — 인앱 전용(결정 #9) */
export interface Notification {
  id: number;
  notif_type: NotificationType;
  title: string;
  message?: string | null;
  target_role?: UserRole | null;
  target_user_id?: number | null;
  target_driver_id?: number | null;
  ref_type?: string | null;
  ref_id?: number | null;
  is_read: boolean;
  read_at?: DateTimeString | null;
  created_at: DateTimeString;
}

/** 바뀐 칸 하나. `생성`·`삭제` 는 한쪽이 비어 있다 */
export interface AuditFieldChange {
  /** 컬럼 이름 그대로 — 툴팁에 넣는다 */
  field: string;
  /** 한국어 칸 이름. 사전에 없으면 `field` 와 같다 */
  label: string;
  before_value?: unknown;
  after_value?: unknown;
}

/**
 * `audit_logs` 한 줄 (`GET /api/audit-logs`).
 *
 * **원본 `before`/`after` 는 오지 않는다** — 서버가 바뀐 칸만 추려 `changes` 로 준다.
 * 스냅샷에는 내부 경로(`seal_path`·`file_path`)가 섞여 있고 화면이 그릴 것도 아니다.
 */
export interface AuditLog {
  id: number;
  actor_type: AuditActorType;
  actor_id?: number | null;
  /** 기록 당시 이름. **계정을 지워도 남는다**(`actor_id` 에 FK 가 없다) */
  actor_name?: string | null;
  action: string;
  entity_type: string;
  /** 한국어 이름. 사전에 없으면 `entity_type` 과 같다 */
  entity_label: string;
  entity_id?: number | null;
  /** 왜 이 줄이 생겼나 — 사유이면서 일괄 작업의 상관관계 키다 */
  reason?: string | null;
  ip_address?: string | null;
  created_at: DateTimeString;
  changes: AuditFieldChange[];
  /**
   * `before`·`after` 가 **둘 다 있어 전후를 비교한** 줄인가 — `전 → 후` 를 그릴지
   * 값 하나만 그릴지 가른다. 칸의 값으로 유추할 수 없다: 값을 **비우기만 한 수정**은
   * 모든 칸이 `값 → null` 이라 유추하면 *비웠다* 가 *있었다* 로 거꾸로 읽힌다.
   */
  compared: boolean;
}

/** `행위`·`대상` 드롭다운 한 칸 */
export interface AuditEnumOption {
  value: string;
  label: string;
}

/** `계정` 드롭다운 한 칸. `value` 는 `system` | `user:{id}` | `driver:{id}` */
export interface AuditActorOption {
  value: string;
  label: string;
  /** `users`·`drivers` 에 더 없는 계정 — 이력만 남았다 */
  deleted: boolean;
}

/** 필터 후보. **로그에 실제로 쌓인 값**만 담는다 */
export interface AuditFilterOptions {
  actors: AuditActorOption[];
  actions: AuditEnumOption[];
  entity_types: AuditEnumOption[];
}

/** `driver_disputes` */
export interface DriverDispute {
  id: number;
  driver_id: number;
  driver_name?: string | null;
  target_month: MonthString;
  dispatch_id?: number | null;
  issue_type: DisputeIssueType;
  content: string;
  status: DisputeStatus;
  response?: string | null;
  handled_by?: number | null;
  handled_by_name?: string | null;
  driver_code?: string | null;
  dispatch_no?: string | null;
  created_at: DateTimeString;
  resolved_at?: DateTimeString | null;
}

/** `vehicle_change_requests` */
export interface VehicleChangeRequest {
  id: number;
  driver_id: number;
  driver_name?: string | null;
  driver_code?: string | null;
  current_vehicle_id?: number | null;
  current_vehicle_number?: string | null;
  requested_vehicle_number: string;
  reason?: string | null;
  desired_date?: DateString | null;
  status: VehicleChangeRequestStatus;
  /** 담당자 응답 — 반려 사유 또는 승인 메모 */
  response?: string | null;
  /** 담당자가 확정한 실제 차량 (희망 문자열과 다를 수 있다) */
  approved_vehicle_id?: number | null;
  approved_vehicle_number?: string | null;
  applied_date?: DateString | null;
  /** 승인으로 만들어진 배정 */
  assignment_id?: number | null;
  /** 서버가 희망 차량번호로 찾아 둔 후보. 확정은 담당자가 한다 */
  requested_vehicle_id?: number | null;
  requested_vehicle_matched?: boolean;
  handled_by?: number | null;
  handled_by_name?: string | null;
  created_at: DateTimeString;
  handled_at?: DateTimeString | null;
}

/** `notices` */
export interface Notice extends Timestamped {
  id: number;
  title: string;
  content?: string | null;
  is_pinned: boolean;
  published_at?: DateTimeString | null;
  created_by?: number | null;
  created_by_name?: string | null;
}

// ---------------------------------------------------------------------------
// Enum 값 목록 — dropdown 옵션 구성을 위해 런타임 배열로도 노출한다.
// ---------------------------------------------------------------------------

export const DIRECTIONS: readonly Direction[] = ["상행", "하행"] as const;
export const RATE_TYPES: readonly RateType[] = ["톤당", "회당"] as const;
export const DISPATCH_TYPES: readonly DispatchType[] = ["소속", "외부"] as const;
export const DISPATCH_STATUSES: readonly DispatchStatus[] = [
  "운행예정",
  "운행중",
  "완료",
  "취소",
] as const;
export const DISPATCH_TRIP_STATUSES: readonly DispatchTripStatus[] = [
  "예정",
  "진행",
  "완료",
] as const;
export const INVOICE_TYPES: readonly InvoiceType[] = ["상차", "하차"] as const;
export const INVOICE_VERIFICATION_STATUSES: readonly InvoiceVerificationStatus[] = [
  "확인중",
  "검증완료",
  "반려",
  "미매칭",
  "중복",
] as const;
export const PERFORMANCE_STATUSES: readonly PerformanceStatus[] = [
  "가실적",
  "확정",
] as const;
export const SETTLEMENT_TYPES: readonly SettlementType[] = ["매출", "매입"] as const;
export const SETTLEMENT_PERIOD_TYPES: readonly SettlementPeriodType[] = [
  "월별",
  "분기",
  "상반기",
  "하반기",
  "년도별",
  "기간",
] as const;
export const VEHICLE_STATUSES: readonly VehicleStatus[] = [
  "운행가능",
  "정비중",
  "폐차",
] as const;
export const DRIVER_TYPES: readonly DriverType[] = [
  "소속기사",
  "개인사업자",
  "외주기사",
] as const;
export const DRIVER_STATUSES: readonly DriverStatus[] = ["활성", "휴직", "퇴사"] as const;
export const DRIVER_AUTH_SOURCES: readonly DriverAuthSource[] = ["자체", "1차"] as const;
export const DISPUTE_STATUSES: readonly DisputeStatus[] = ["접수", "검토중", "완료"] as const;
export const DISPUTE_ISSUE_TYPES: readonly DisputeIssueType[] = [
  "톤수불일치",
  "단가오류",
  "누락",
  "기타",
] as const;
export const VEHICLE_CHANGE_REQUEST_STATUSES: readonly VehicleChangeRequestStatus[] = [
  "접수",
  "승인",
  "반려",
] as const;
export const LOCATION_TYPES: readonly LocationType[] = [
  "상차지",
  "하차지",
  "상하차지",
] as const;
export const PURCHASE_TARGET_TYPES: readonly PurchaseTargetType[] = [
  "개인사업자",
  "소속기사",
  "외주업체",
] as const;
export const EXCEL_UPLOAD_TYPES: readonly ExcelUploadType[] = [
  "배차",
  "MES상행실적",
  "상행운송내역",
  "차량등록정보",
  "기준정보",
] as const;


// ---------------------------------------------------------------------------
// 구간 빠른 등록 (담당자 피드백 2026-09-03)
// ---------------------------------------------------------------------------

/** 상차지·하차지 지정. `location_id`(기존) 와 `name`(없으면 새로) 중 정확히 하나 */
export interface QuickLocationRef {
  location_id?: number | null;
  name?: string | null;
  location_type?: LocationType | null;
  aliases?: string[];
}

export interface QuickSalesPriceInput {
  /** 청구처 — 매출에만 쓴다. 비우면 청구처 공통 (2026-10-30) */
  company_id?: number | null;
  rate_type: RateType;
  unit_price: number;
  applies_from: DateString;
  applies_to?: DateString | null;
  contract_code?: string | null;
  /** 옛 운송코드(`G0104`). 040 이 `a1`~ 로 바꿀 때 남긴 원문 */
  legacy_contract_code?: string | null;
  memo?: string | null;
}

export interface QuickPurchasePriceInput extends QuickSalesPriceInput {
  target_type: PurchaseTargetType;
}

export interface RouteQuickCreate {
  departure: QuickLocationRef;
  arrival: QuickLocationRef;
  direction: Direction;
  default_item_id?: number | null;
  default_tonnage?: number | null;
  /** 구간 식별색 (`#RRGGBB`). 만드는 자리에서 함께 고른다 (2026-09-17) */
  color?: string | null;
  distance_km?: number | null;
  sales_price?: QuickSalesPriceInput | null;
  /** 업체 줄 — 기사와 단가가 다르다 */
  purchase_company_price?: QuickPurchasePriceInput | null;
  /** 기사 줄 */
  purchase_driver_price?: QuickPurchasePriceInput | null;
}

export interface QuickLocationResult {
  location_id: number;
  name: string;
  created: boolean;
  /** 재사용했다면 무엇으로 찾았는지 — 표준명·별칭·정규화 */
  matched_by?: string | null;
}

export interface RouteQuickResponse {
  route: Route;
  route_created: boolean;
  /** 배차가 만들어 둔 비활성 구간을 켰는지 */
  route_activated: boolean;
  departure: QuickLocationResult;
  arrival: QuickLocationResult;
  sales_price_id?: number | null;
  purchase_price_ids: number[];
  repriced_performances: number;
  warnings: string[];
}

// ---------------------------------------------------------------------------
// 운영·진단 (API-OPS) — 시스템관리자 전용. 사이드바에 없고 주소로만 들어간다
// ---------------------------------------------------------------------------

/** 한 묶음(월·제출 경로·합계)의 토큰과 비용 */
export interface OcrCostRow {
  label: string;
  invoice_count: number;
  input_tokens: number;
  output_tokens: number;
  /** 원. 조회 시점 단가·환율을 곱한 추정치다 */
  cost_krw: number;
  cost_per_invoice_krw: number;
}

/** 엔진별 송장 수. `with_usage_count` 가 비용 계산에 들어간 장수다 */
export interface OcrEngineRow {
  provider: string;
  invoice_count: number;
  with_usage_count: number;
}

export interface OcrCostReport {
  date_from: string | null;
  date_to: string | null;
  model: string;
  input_price: number;
  output_price: number;
  usd_krw: number;
  /** OCR 큐 재시도 상한. 재시도분은 `ocr_raw` 에 안 남아 비용에서 빠진다 */
  max_attempts: number;
  total: OcrCostRow;
  monthly: OcrCostRow[];
  by_source: OcrCostRow[];
  engines: OcrEngineRow[];
  /** `llm` 인데 토큰이 안 남은 장수 — 호출 전에 실패했거나 응답을 못 받았다 */
  missing_usage_count: number;
}

/** 운영 → 개발 복사의 마지막 실행 (API-OPS-002) — 스크립트가 남긴 `last.json` */
export interface ProdCopyLastRun {
  started_at: string | null;
  finished_at: string | null;
  /** `success` | `failed` */
  result: string;
  error: string | null;
  /** `rev=048 users_kept=… ocr_stopped=…` */
  detail: string | null;
}

export interface ProdCopyStatus {
  /** 이 서버에 복사 배치가 설치돼 있는가 */
  available: boolean;
  running: boolean;
  last_run: ProdCopyLastRun | null;
}
